import Button from "components/Button/Button"
import PageLayout from "pages/layout/PageLayout"
import FormInputCard from "./molecules/FormInputCard"
import InputPhone from "components/Input/InputPhone"
import { useBoolean, useString } from "helpers/hooks"
import InputDefault from "components/Input/InputDefault"
import { useChangeLabelStatusInputPasswordAndConfirm } from "./auth.hook"
import { STATUS_INPUT } from "components/Input/types"
import { downLoadBackUpKey, isValidPhone } from "helpers/utils"
import {
  getUserMiddleware,
  postGenerateNewRecoveryCodeMiddleware,
  postSendRecoveryCodeMiddleware,
  postUpdateSecurityCodeAndPhoneNumberMiddleware,
} from "./services/api"
import LabelNotificationPage from "components/Notification/LabelNotificationPage"
import { toast } from "react-toastify"
import {
  EncryptionKeys,
  encryptionController,
} from "controllers/EncryptionController"
import { pushTo } from "helpers/history"
import { PATH } from "constants/path"
import { encryptionGCMHelper } from "helpers/encryption-gcm"
import { useAppSelector } from "hooks/useApp"
import { useEffect } from "react"
import { encryptionHelper } from "helpers/encryption"
import RecoveryModal from "./molecules/RecoveryModal"
import { useLocation } from "react-router-dom"
import { useHistory } from "react-router-dom"
import { NAME_LOCALSTORAGE } from "constants/localStorage"

export const UpdatePhoneNumberAndSecurityCode = () => {
  const location = useLocation() as any
  const history = useHistory()
  const userInfo = useAppSelector((state) => state.userInfo)
  const phone = useString("")
  const password = useString("")
  const securityCode = useString("")
  const backUpKeyState = useString()
  const isSuccessPasswordState = useBoolean(false)
  const { statusInputPassword, getStatusInputPassword } =
    useChangeLabelStatusInputPasswordAndConfirm()

  useEffect(() => {
    if (location.state?.isSuccessPassword) {
      isSuccessPasswordState.setValue(true)
      const { isSuccessPassword, currentPassword, ...rest } =
        location.state || {}
      if (currentPassword) {
        password.setValue(currentPassword)
      }
      history.replace({ ...location, state: rest })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, history])

  useEffect(() => {
    if (backUpKeyState.value) {
      return
    }
    if (isValidPhone(userInfo.phone_number)) {
      pushTo(PATH.general, undefined, { tab: 2 })
    }
  }, [userInfo.id, userInfo.phone_number, backUpKeyState.value])

  const onKeyPress = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === "Enter") {
      event.preventDefault()
    }
  }
  const handleChangePhoneNumber = (value) => {
    phone.setValue(value)
  }

  const handleChangeInput = (event) => {
    password.setValue(event.target.value)
    getStatusInputPassword(event.target.value)
  }

  const onClickButtonSave = async () => {
    const { generatePasswordHashed } = encryptionController()
    const vaultKey = localStorage.getItem(EncryptionKeys.userVaultKey) || ""
    const request = {
      phoneNumber: phone.value,
      password: await generatePasswordHashed(password.value),
    }

    postUpdateSecurityCodeAndPhoneNumberMiddleware(request)
      .then(async () => {
        toast(
          <LabelNotificationPage
            messenger="Updated phone number successfully!"
            type="success"
          />
        )
        const backupKeyCode = encryptionHelper.createBackupKey()
        const encryptedBackupKeyCode = await encryptionGCMHelper.encryptContext(
          backupKeyCode,
          vaultKey,
          "passphrase"
        )
        await postGenerateNewRecoveryCodeMiddleware(encryptedBackupKeyCode)
        backUpKeyState.setValue(backupKeyCode)
        getUserMiddleware()
        localStorage.removeItem(NAME_LOCALSTORAGE.REMIND_ME_LATER)
        await postSendRecoveryCodeMiddleware(
          phone.value,
          securityCode.value,
          backupKeyCode,
          userInfo.email,
          userInfo.fullname
        )
      })
      .catch((error) => {
        const message = error.response?.data?.message ?? "Something went wrong!"
        toast(<LabelNotificationPage messenger={message} type="error" />)
      })
  }

  const onResendEmail = () => {
    postSendRecoveryCodeMiddleware(
      phone.value,
      securityCode.value,
      backUpKeyState.value,
      userInfo.email,
      userInfo.fullname
    )
  }

  const isValidPhoneNumber = isValidPhone(phone.value)

  const handleDisableButton = () => {
    if (
      securityCode.value.length === 4 &&
      isValidPhoneNumber &&
      (statusInputPassword.status === STATUS_INPUT.VALID ||
        isSuccessPasswordState.value)
    ) {
      return false
    }
    return true
  }

  return (
    <PageLayout heightHeader={0} minWidth="min-w-[500px]">
      <div className="flex items-center justify-center h-full">
        <div
          className="bg-white p-[24px] gap-[12px] flex flex-col max-w-[440px] w-full mx-[16px]"
          style={{
            border: "1px solid #E4E4E4",
            borderRadius: 6,
          }}
        >
          <p className="text-[#111111] text-[20px] font-bold leading-8">
            Secure Your Account
          </p>
          <div className="flex flex-col">
            <p className="text-[#111111] text-[14px] font-bold mb-[24px]">
              Please update your details
            </p>
            <FormInputCard title="Phone number" required>
              <InputPhone
                value={phone.value}
                setValue={handleChangePhoneNumber}
                onEnterKeyPress={onKeyPress}
              />
              {phone.value ? (
                <div className="flex mt-1">
                  <p
                    className="font-semibold"
                    style={{
                      color: isValidPhoneNumber ? "#56A012" : "#EA4545",
                      fontSize: 10,
                      lineHeight: "14px",
                    }}
                  >
                    {isValidPhoneNumber
                      ? "Valid phone number format!"
                      : "Invalid phone number format!"}
                  </p>
                </div>
              ) : null}
            </FormInputCard>
            <FormInputCard title="Set up a passcode" required>
              <InputDefault
                value={securityCode.value}
                onChange={(el) => securityCode.setValue(el.target.value)}
                placeholder="4 characters"
                maxLength={4}
                onKeyPress={onKeyPress}
                type="password"
                isPassword
              />
              <div className="flex flex-col mt-1">
                <p className="text-[#111111] text-[13px] leading-[22px]">
                  Your new recovery code file will be password-protected and
                  sent to you via email. To unlock it, use a code composed of
                  the last 4 digits of your phone number, followed by your
                  4-character passcode.Your passcode may include alphanumeric
                  and special characters, it is case-sensitive.
                </p>
                <p className="font-semibold text-[#111111] text-[13px] leading-[22px]">
                  Format:[Last 4 digits of phone][4-character passcode]
                </p>
                <p className=" text-[#111111] text-[13px] leading-[22px]">
                  Example: If your phone number is 123456 and your passcode is
                  ABCD, the unlock code is 3456ABCD.If your passcode is @$cd,
                  then your unlock code is 1234@$cd.
                </p>
                <p className="text-[#111111] text-[13px] leading-[22px]">
                  Please note that your passcode is not stored, and we cannot
                  recover your file if it is lost.
                </p>
              </div>
            </FormInputCard>
            {!isSuccessPasswordState.value && (
              <FormInputCard required title="Current Password">
                <InputDefault
                  type="password"
                  isPassword
                  value={password.value}
                  onChange={handleChangeInput}
                  onKeyPress={onKeyPress}
                  status={statusInputPassword.status}
                  labelStatus={statusInputPassword.label}
                />
              </FormInputCard>
            )}
          </div>
          <Button
            title="Done"
            widthBtn={"100%"}
            onClick={onClickButtonSave}
            disabled={handleDisableButton()}
            isDisabledBtn
          />
        </div>
      </div>
      {backUpKeyState.value && (
        <RecoveryModal
          backUpKey={backUpKeyState.value}
          onResendEmail={onResendEmail}
          onClickSubmit={() => {
            downLoadBackUpKey(
              backUpKeyState.value,
              userInfo.email,
              phone.value,
              securityCode.value
            )
          }}
          onCloseModal={() => {
            backUpKeyState.setValue("")
            setTimeout(() => {
              pushTo(PATH.general, undefined, { tab: 2 })
            }, 200)
          }}
        />
      )}
    </PageLayout>
  )
}
